/**
 * FinanceHeaderSearch — Compact search bar for Finance module header.
 *
 * Provides quick access to search without going back to Finance start page.
 * Same functionality as FinanceStartPage search but in a compact header format.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Search,
  ArrowRight,
  Users,
  BookOpen,
  Banknote,
  ArrowDownLeft,
  ArrowUpRight,
  Loader2,
  FileText,
  Receipt,
  X,
  LayoutDashboard,
  Link2,
  CreditCard,
  Calculator,
  Scale,
  TrendingUp,
  Clock,
  FileSpreadsheet,
  Settings,
  Plus,
  BarChart3,
  Landmark,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { useTaxLabel } from '@/hooks/useTenantProfile'
import { useFinanceSearch } from '../../hooks/useFinanceSearch'
import { QuickLookupModal } from './QuickLookupModal'

// ---------------------------------------------------------------------------
// Static searchable items (same as FinanceStartPage)
// ---------------------------------------------------------------------------

interface SearchableItem {
  id: string
  category: 'action' | 'page' | 'report'
  title: string
  subtitle?: string
  keywords: string[]
  href: string
  icon: React.ElementType
}

const SEARCHABLE_ITEMS: SearchableItem[] = [
  // Quick Actions
  {
    id: 'action-new-payment',
    category: 'action',
    title: 'New Payment',
    subtitle: 'Record a vendor payment',
    keywords: ['payment', 'pay', 'vendor', 'create', 'new', 'pv'],
    href: '/finance/transactions?action=new-payment',
    icon: ArrowUpRight,
  },
  {
    id: 'action-new-receipt',
    category: 'action',
    title: 'New Receipt',
    subtitle: 'Record customer payment',
    keywords: ['receipt', 'receive', 'customer', 'create', 'new', 'rv', 'collection'],
    href: '/finance/transactions?action=new-receipt',
    icon: ArrowDownLeft,
  },
  {
    id: 'action-new-journal',
    category: 'action',
    title: 'New Journal Entry',
    subtitle: 'Create a journal voucher',
    keywords: ['journal', 'jv', 'entry', 'create', 'new', 'adjustment'],
    href: '/finance/transactions?action=new-journal',
    icon: FileText,
  },
  {
    id: 'action-new-invoice',
    category: 'action',
    title: 'New Sales Invoice',
    subtitle: 'Create a sales invoice from job order',
    keywords: ['invoice', 'sales', 'si', 'create', 'new', 'bill', 'customer'],
    href: '/finance/invoices?action=new-sales',
    icon: Receipt,
  },
  {
    id: 'action-new-purchase-invoice',
    category: 'action',
    title: 'New Purchase Invoice',
    subtitle: 'Record a vendor bill',
    keywords: ['purchase', 'invoice', 'pi', 'create', 'new', 'bill', 'vendor', 'expense'],
    href: '/finance/invoices?action=new-purchase',
    icon: Receipt,
  },
  {
    id: 'action-new-credit-note',
    category: 'action',
    title: 'New Credit Note',
    subtitle: 'Issue a credit note',
    keywords: ['credit', 'note', 'cn', 'create', 'new', 'refund', 'return', 'sales return'],
    href: '/finance/invoices?action=credit-note',
    icon: Receipt,
  },
  {
    id: 'action-new-debit-note',
    category: 'action',
    title: 'New Debit Note',
    subtitle: 'Issue a debit note',
    keywords: ['debit', 'note', 'dn', 'create', 'new', 'purchase return', 'vendor return'],
    href: '/finance/transactions?action=new-debit-note',
    icon: Receipt,
  },
  {
    id: 'action-allocate',
    category: 'action',
    title: 'Allocate Payments',
    subtitle: 'Match payments to invoices',
    keywords: ['allocate', 'allocation', 'match', 'apply'],
    href: '/finance/allocations',
    icon: Link2,
  },

  // Pages
  {
    id: 'page-dashboard',
    category: 'page',
    title: 'Dashboard',
    subtitle: 'Daily overview & KPIs',
    keywords: ['dashboard', 'home', 'overview', 'kpi', 'summary', 'daily'],
    href: '/finance/dashboard',
    icon: LayoutDashboard,
  },
  {
    id: 'page-transactions',
    category: 'page',
    title: 'All Transactions',
    subtitle: 'View all vouchers',
    keywords: ['transactions', 'vouchers', 'all', 'list', 'payments', 'receipts'],
    href: '/finance/transactions',
    icon: FileText,
  },
  {
    id: 'page-invoices',
    category: 'page',
    title: 'Invoices',
    subtitle: 'Sales & purchase invoices',
    keywords: ['invoices', 'invoice', 'sales invoice', 'purchase invoice', 'si', 'pi', 'billing', 'bills'],
    href: '/finance/invoices',
    icon: Receipt,
  },
  {
    id: 'page-allocations',
    category: 'page',
    title: 'Allocations',
    subtitle: 'Match payments to invoices',
    keywords: ['allocations', 'allocate', 'match', 'apply', 'unallocated'],
    href: '/finance/allocations',
    icon: Link2,
  },
  {
    id: 'page-collections',
    category: 'page',
    title: 'Collections',
    subtitle: 'Customer receivables & follow-up',
    keywords: ['collections', 'receivables', 'customers', 'outstanding', 'overdue', 'ar'],
    href: '/finance/receivables',
    icon: ArrowDownLeft,
  },
  {
    id: 'page-payables',
    category: 'page',
    title: 'Payments Due',
    subtitle: 'Vendor payables & scheduling',
    keywords: ['payables', 'payments due', 'vendors', 'outstanding', 'ap', 'bills'],
    href: '/finance/payables',
    icon: ArrowUpRight,
  },
  {
    id: 'page-cheques',
    category: 'page',
    title: 'Cheque Tracking',
    subtitle: 'PDC register & status',
    keywords: ['cheques', 'checks', 'pdc', 'post-dated', 'register', 'clearing'],
    href: '/finance/cheques',
    icon: CreditCard,
  },
  {
    id: 'page-accounts',
    category: 'page',
    title: 'Chart of Accounts',
    subtitle: 'GL account master',
    keywords: ['accounts', 'chart', 'coa', 'ledger', 'gl', 'master'],
    href: '/finance/masters/accounts',
    icon: BookOpen,
  },
  {
    id: 'page-parties',
    category: 'page',
    title: 'Customers & Vendors',
    subtitle: 'Subledger accounts',
    keywords: ['customers', 'vendors', 'parties', 'subledger', 'master'],
    href: '/finance/masters/parties',
    icon: Users,
  },
  {
    id: 'page-settings',
    category: 'page',
    title: 'Finance Config',
    subtitle: 'Settings & configuration',
    keywords: ['settings', 'config', 'configuration', 'setup', 'periods', 'voucher types'],
    href: '/finance/settings',
    icon: Settings,
  },

  // Reports
  {
    id: 'report-hub',
    category: 'report',
    title: 'Reports Hub',
    subtitle: 'All financial reports',
    keywords: ['reports', 'hub', 'all reports'],
    href: '/finance/reports',
    icon: BarChart3,
  },
  {
    id: 'report-balance-sheet',
    category: 'report',
    title: 'Balance Sheet',
    subtitle: 'Assets, liabilities, equity',
    keywords: ['balance sheet', 'bs', 'assets', 'liabilities', 'equity', 'position'],
    href: '/finance/reports/balance-sheet',
    icon: Scale,
  },
  {
    id: 'report-profit-loss',
    category: 'report',
    title: 'Profit & Loss',
    subtitle: 'Income statement',
    keywords: ['profit', 'loss', 'p&l', 'pl', 'income', 'statement', 'revenue', 'expenses'],
    href: '/finance/reports/profit-loss',
    icon: TrendingUp,
  },
  {
    id: 'report-trial-balance',
    category: 'report',
    title: 'Trial Balance',
    subtitle: 'Account balances summary',
    keywords: ['trial balance', 'tb', 'balances', 'debit', 'credit'],
    href: '/finance/reports/trial-balance',
    icon: Calculator,
  },
  {
    id: 'report-general-ledger',
    category: 'report',
    title: 'General Ledger',
    subtitle: 'Account transactions detail',
    keywords: ['general ledger', 'gl', 'ledger', 'transactions', 'detail'],
    href: '/finance/reports/general-ledger',
    icon: BookOpen,
  },
  {
    id: 'report-aging-receivables',
    category: 'report',
    title: 'Receivables Aging',
    subtitle: 'Customer aging analysis',
    keywords: ['aging', 'receivables', 'ar aging', 'customer', 'overdue', '30 60 90'],
    href: '/finance/reports/aging/receivables',
    icon: Clock,
  },
  {
    id: 'report-aging-payables',
    category: 'report',
    title: 'Payables Aging',
    subtitle: 'Vendor aging analysis',
    keywords: ['aging', 'payables', 'ap aging', 'vendor', 'overdue'],
    href: '/finance/reports/aging/payables',
    icon: Clock,
  },
  {
    id: 'report-customer-statement',
    category: 'report',
    title: 'Customer Statement',
    subtitle: 'Customer transaction history',
    keywords: ['customer statement', 'statement', 'account statement', 'history'],
    href: '/finance/reports/statement/customer',
    icon: FileSpreadsheet,
  },
  {
    id: 'report-vendor-statement',
    category: 'report',
    title: 'Vendor Statement',
    subtitle: 'Vendor transaction history',
    keywords: ['vendor statement', 'statement', 'supplier', 'history'],
    href: '/finance/reports/statement/vendor',
    icon: FileSpreadsheet,
  },
  {
    id: 'report-vat',
    category: 'report',
    title: 'VAT Report',
    subtitle: 'Tax summary for filing',
    keywords: ['vat', 'tax', 'gst', 'report', 'filing', 'return'],
    href: '/finance/reports/vat',
    icon: FileSpreadsheet,
  },
  {
    id: 'report-cash-position',
    category: 'report',
    title: 'Cash Position',
    subtitle: 'Bank balances overview',
    keywords: ['cash', 'position', 'bank', 'balances', 'liquidity'],
    href: '/finance/reports/cash-position',
    icon: Landmark,
  },
]

function filterSearchableItems(query: string): SearchableItem[] {
  if (!query || query.trim().length < 2) return []

  const q = query.toLowerCase().trim()

  return SEARCHABLE_ITEMS.filter((item) => {
    if (item.title.toLowerCase().includes(q)) return true
    if (item.subtitle?.toLowerCase().includes(q)) return true
    if (item.keywords.some((kw) => kw.toLowerCase().includes(q))) return true
    return false
  })
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FinanceHeaderSearch() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchQuery, setSearchQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const taxLabel = useTaxLabel()

  // Reset search state when route changes
  useEffect(() => {
    setSearchQuery('')
    setIsFocused(false)
    setSelectedIndex(-1)
    inputRef.current?.blur()
  }, [location.pathname])

  // Local search (instant). The VAT report entry resolves its DISPLAY title
  // from the tenant tax label (id/href/keywords stay stable).
  const localResults = useMemo(
    () =>
      filterSearchableItems(searchQuery).map((item) =>
        item.id === 'report-vat' ? { ...item, title: `${taxLabel} Report` } : item,
      ),
    [searchQuery, taxLabel],
  )
  const localActions = useMemo(() => localResults.filter((r) => r.category === 'action'), [localResults])
  const localPages = useMemo(() => localResults.filter((r) => r.category === 'page'), [localResults])
  const localReports = useMemo(() => localResults.filter((r) => r.category === 'report'), [localResults])
  const hasLocalResults = localResults.length > 0

  // API search (debounced)
  const { results: apiResults, isSearching, hasResults: hasApiResults } = useFinanceSearch(searchQuery)

  // Quick Lookup Modal state
  const [lookupSubledgerId, setLookupSubledgerId] = useState<string | null>(null)

  // Show dropdown
  const showDropdown = isFocused && searchQuery.length >= 2 && (hasLocalResults || isSearching || hasApiResults)

  // Flatten results for keyboard navigation
  interface FlatResult {
    type: 'local' | 'customer' | 'voucher' | 'ledger'
    item: SearchableItem | { id: string; name?: string | null; code?: string | null; type?: string; voucherNo?: string | null; narration?: string | null }
  }

  const flatResults = useMemo<FlatResult[]>(() => {
    const results: FlatResult[] = []
    localActions.slice(0, 3).forEach((item) => results.push({ type: 'local', item }))
    localPages.slice(0, 4).forEach((item) => results.push({ type: 'local', item }))
    localReports.slice(0, 4).forEach((item) => results.push({ type: 'local', item }))
    apiResults?.customers?.forEach((item) => results.push({ type: 'customer', item }))
    apiResults?.vouchers?.forEach((item) => results.push({ type: 'voucher', item }))
    apiResults?.ledgers?.forEach((item) => results.push({ type: 'ledger', item }))
    return results
  }, [localActions, localPages, localReports, apiResults])

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1)
  }, [searchQuery, flatResults.length])

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0) {
      const el = document.querySelector(`[data-header-search-idx="${selectedIndex}"]`)
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [selectedIndex])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle search result click
  const handleSearchResultClick = useCallback(
    (type: 'customer' | 'vendor' | 'voucher' | 'ledger', id: string, name: string) => {
      if (type === 'customer' || type === 'vendor') {
        setLookupSubledgerId(id)
      } else if (type === 'voucher') {
        navigate(`/finance/transactions?selected=${id}`)
      } else if (type === 'ledger') {
        navigate(`/finance/reports/general-ledger?ledgerId=${id}`)
      }
      setSearchQuery('')
      setIsFocused(false)
    },
    [navigate],
  )

  // Handle local result click
  const handleLocalResultClick = useCallback(
    (item: SearchableItem) => {
      navigate(item.href)
      setSearchQuery('')
      setIsFocused(false)
    },
    [navigate],
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown || flatResults.length === 0) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : 0))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : flatResults.length - 1))
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault()
        const selected = flatResults[selectedIndex]
        if (selected.type === 'local') {
          handleLocalResultClick(selected.item as SearchableItem)
        } else if (selected.type === 'customer') {
          const item = selected.item as { id: string; name?: string | null; type?: string }
          handleSearchResultClick(
            item.type === 'CUSTOMER' ? 'customer' : 'vendor',
            item.id,
            item.name ?? '',
          )
        } else if (selected.type === 'voucher') {
          const item = selected.item as { id: string; voucherNo?: string | null }
          handleSearchResultClick('voucher', item.id, item.voucherNo ?? '')
        } else if (selected.type === 'ledger') {
          const item = selected.item as { id: string; name?: string | null }
          handleSearchResultClick('ledger', item.id, item.name ?? '')
        }
      } else if (e.key === 'Escape') {
        setSearchQuery('')
        setSelectedIndex(-1)
        setIsFocused(false)
        inputRef.current?.blur()
      }
    },
    [showDropdown, flatResults, selectedIndex, handleLocalResultClick, handleSearchResultClick],
  )

  return (
    <>
      <div ref={containerRef} className="relative">
        {/* Compact search input */}
        <div className="relative rainbow-border-sm rounded-lg">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground z-10" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            className="h-8 w-48 pl-8 pr-14 text-sm rounded-lg border-0 bg-white focus:bg-white focus:w-64 transition-all focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          {/* AI Badge */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-medium">
            <Sparkles className="size-2.5" />
            <span>AI</span>
          </div>
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('')
                inputRef.current?.focus()
              }}
              className="absolute right-12 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        {/* Search Results Dropdown */}
        {showDropdown && (
          <div className="absolute top-full right-0 mt-1 w-80 rounded-lg border bg-card shadow-lg z-50 overflow-hidden">
            <div className="max-h-80 overflow-y-auto divide-y">
              {/* Quick Actions */}
              {localActions.length > 0 && (
                <div className="p-1.5">
                  <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Actions</p>
                  {localActions.slice(0, 3).map((item, idx) => {
                    const Icon = item.icon
                    const flatIdx = idx
                    return (
                      <button
                        key={item.id}
                        data-header-search-idx={flatIdx}
                        onClick={() => handleLocalResultClick(item)}
                        className={cn(
                          'flex w-full items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-left',
                          selectedIndex === flatIdx ? 'bg-muted' : 'hover:bg-muted',
                        )}
                      >
                        <div className="flex size-6 items-center justify-center rounded bg-primary/10">
                          <Icon className="size-3 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.title}</p>
                        </div>
                        <Plus className="size-3.5 text-muted-foreground shrink-0" />
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Pages */}
              {localPages.length > 0 && (
                <div className="p-1.5">
                  <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Pages</p>
                  {localPages.slice(0, 4).map((item, idx) => {
                    const Icon = item.icon
                    const flatIdx = Math.min(localActions.length, 3) + idx
                    return (
                      <button
                        key={item.id}
                        data-header-search-idx={flatIdx}
                        onClick={() => handleLocalResultClick(item)}
                        className={cn(
                          'flex w-full items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-left',
                          selectedIndex === flatIdx ? 'bg-muted' : 'hover:bg-muted',
                        )}
                      >
                        <Icon className="size-4 text-muted-foreground shrink-0" />
                        <span className="text-sm truncate">{item.title}</span>
                        <ArrowRight className="size-3.5 text-muted-foreground shrink-0 ml-auto" />
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Reports */}
              {localReports.length > 0 && (
                <div className="p-1.5">
                  <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Reports</p>
                  {localReports.slice(0, 4).map((item, idx) => {
                    const Icon = item.icon
                    const flatIdx = Math.min(localActions.length, 3) + Math.min(localPages.length, 4) + idx
                    return (
                      <button
                        key={item.id}
                        data-header-search-idx={flatIdx}
                        onClick={() => handleLocalResultClick(item)}
                        className={cn(
                          'flex w-full items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-left',
                          selectedIndex === flatIdx ? 'bg-muted' : 'hover:bg-muted',
                        )}
                      >
                        <Icon className="size-4 text-muted-foreground shrink-0" />
                        <span className="text-sm truncate">{item.title}</span>
                        <ArrowRight className="size-3.5 text-muted-foreground shrink-0 ml-auto" />
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Customers & Vendors */}
              {apiResults?.customers && apiResults.customers.length > 0 && (
                <div className="p-1.5">
                  <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Customers & Vendors</p>
                  {apiResults.customers.map((item, idx) => {
                    const localCount = Math.min(localActions.length, 3) + Math.min(localPages.length, 4) + Math.min(localReports.length, 4)
                    const flatIdx = localCount + idx
                    return (
                      <button
                        key={item.id}
                        data-header-search-idx={flatIdx}
                        onClick={() =>
                          handleSearchResultClick(
                            item.type === 'CUSTOMER' ? 'customer' : 'vendor',
                            item.id,
                            item.name ?? '',
                          )
                        }
                        className={cn(
                          'flex w-full items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-left',
                          selectedIndex === flatIdx ? 'bg-muted' : 'hover:bg-muted',
                        )}
                      >
                        <Users className="size-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{item.name}</p>
                          <p className="text-[10px] text-muted-foreground">{item.code}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Vouchers */}
              {apiResults?.vouchers && apiResults.vouchers.length > 0 && (
                <div className="p-1.5">
                  <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Vouchers</p>
                  {apiResults.vouchers.map((item, idx) => {
                    const VIcon = item.type.includes('RECEIPT')
                      ? ArrowDownLeft
                      : item.type.includes('PAYMENT')
                        ? ArrowUpRight
                        : Banknote
                    const localCount = Math.min(localActions.length, 3) + Math.min(localPages.length, 4) + Math.min(localReports.length, 4)
                    const customerCount = apiResults?.customers?.length ?? 0
                    const flatIdx = localCount + customerCount + idx
                    return (
                      <button
                        key={item.id}
                        data-header-search-idx={flatIdx}
                        onClick={() => handleSearchResultClick('voucher', item.id, item.voucherNo ?? '')}
                        className={cn(
                          'flex w-full items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-left',
                          selectedIndex === flatIdx ? 'bg-muted' : 'hover:bg-muted',
                        )}
                      >
                        <VIcon className="size-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-mono truncate">{item.voucherNo}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0 ml-auto">
                          {item.type.replace(/_/g, ' ')}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Ledger Accounts */}
              {apiResults?.ledgers && apiResults.ledgers.length > 0 && (
                <div className="p-1.5">
                  <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Ledger Accounts</p>
                  {apiResults.ledgers.map((item, idx) => {
                    const localCount = Math.min(localActions.length, 3) + Math.min(localPages.length, 4) + Math.min(localReports.length, 4)
                    const customerCount = apiResults?.customers?.length ?? 0
                    const voucherCount = apiResults?.vouchers?.length ?? 0
                    const flatIdx = localCount + customerCount + voucherCount + idx
                    return (
                      <button
                        key={item.id}
                        data-header-search-idx={flatIdx}
                        onClick={() => handleSearchResultClick('ledger', item.id, item.name ?? '')}
                        className={cn(
                          'flex w-full items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-left',
                          selectedIndex === flatIdx ? 'bg-muted' : 'hover:bg-muted',
                        )}
                      >
                        <BookOpen className="size-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{item.name}</p>
                          <p className="text-[10px] text-muted-foreground">{item.code}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Loading indicator */}
              {isSearching && !hasApiResults && !hasLocalResults && (
                <div className="flex items-center justify-center gap-2 py-4">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Searching...</span>
                </div>
              )}

              {/* Subtle loading when we have local results */}
              {isSearching && hasLocalResults && !hasApiResults && (
                <div className="flex items-center justify-center gap-2 py-2 border-t">
                  <Loader2 className="size-3 animate-spin text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">Searching records...</span>
                </div>
              )}

              {/* No results */}
              {!isSearching && searchQuery.length >= 2 && !hasLocalResults && !hasApiResults && (
                <div className="py-4 text-center">
                  <p className="text-xs text-muted-foreground">No results for "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Lookup Modal */}
      <QuickLookupModal
        open={!!lookupSubledgerId}
        subledgerId={lookupSubledgerId}
        onClose={() => setLookupSubledgerId(null)}
      />
    </>
  )
}
